import { Box, Checkbox, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { FieldError } from "@components/FieldError";
import { colors } from "@components/ThemeRegistry/theme";
import { IMultipleSelectFormControlProperty } from "@definitions/index";
import {
	flexCenterProps,
	paddingX1Props,
	requiredLabelStyle,
} from "@styles/global-css-class";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";
import { StyledFormLabel } from "..";

function NewMultipleSelectSimpleFormControl({
	childKey,
	label,
	name,
	value,
	defaultValue,
	onChange,
	isRequired,
	isDisabled,
	isHidden,
	listValues,
	placeHolder,
	formState,
	zIndex,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	errorName,
	isExpanded,
	isHideLabel = true,
	showIndicator,
	isIndicatorTakeSpace,
	isShowRequiredAsterisk = true,
	...props
}: Readonly<IMultipleSelectFormControlProperty>) {
	const [selectedValues, setSelectedValues] = useState<string[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded, inputRef.current]);

	useEffect(() => {
		if (defaultValue?.length > 0) {
			setSelectedValues(defaultValue);
			return;
		}

		const splitValues = typeof value === "string" && value?.split(",").length > 0 ? value.split(",") : value;
		setSelectedValues(splitValues);
	}, [defaultValue, value]);

	useEffect(() => {

	}, [listValues])

	const renderSelectValues = useCallback((selectedValues: string[]) => {
		if (!selectedValues || selectedValues.length == 0) {
			return '';
		}

		const styles = {
			textAlign: "left",
			textOverflow: "ellipsis",
			overflow: "hidden",
			whiteSpace: "nowrap",
		};

		const displayNames: string[] = selectedValues
			.map(value => listValues.find(item => item.value === value)?.name ?? value ?? "");
		return <Box sx={styles}>{displayNames?.join(", ")}</Box>;
	}, [listValues]);

	const handleValueChange = (event: SelectChangeEvent<string | string[]>) => {
		const value = event.target.value;
		const splitValues = typeof value === "string" ? value.split(",") : value;
		setSelectedValues(splitValues);
		onChange(splitValues);
	};

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start'
	}}>
		<FormControl
			key={`${childKey}-form-control`}
			fullWidth margin='normal' {...props}
			error={
				formState?.errors[errorName ?? name] !==
				undefined
			}
		>
			<Grid container sx={flexCenterProps}>
				{!isHideLabel && <Grid xs={xsLabel} sx={itemSx ?? { pr: 1 }}>
					<StyledFormLabel
						aria-describedby={name}
						hidden={isHidden}
						error={formState?.errors[errorName ?? name] !== undefined}
						sx={textSx ?? {
							...requiredLabelStyle,
							justifyContent: textAlign ?? "right",
						}}>
						{label}
						{isRequired && isShowRequiredAsterisk && ' *'}
					</StyledFormLabel>
				</Grid>}
				<Grid container xs={xsInput ?? (!xsLabel || xsLabel === 12 ? 12 : 12 - xsLabel)} sx={{ position: "relative", flexWrap: 'nowrap' }} >
					<Grid item xs={isIndicatorTakeSpace ? 11 : 12}>
						{isHideLabel && <InputLabel
							key={`${childKey}-form-control-input-label`}
							id={name}
							sx={textSx ?? {}}
							error={formState?.errors[errorName ?? name] !== undefined}
						>
							{label}
							{isRequired && isShowRequiredAsterisk && ' *'}
						</InputLabel>}
						<Select
							key={`${childKey}-form-control-select`}
							labelId={`${name}-label`}
							label={isHideLabel ? label : undefined}
							multiple
							value={selectedValues ?? []}
							disabled={isDisabled}
							onChange={handleValueChange}
							renderValue={renderSelectValues}
							inputRef={inputRef}
							sx={{
								width: "100%"
							}}
							MenuProps={{
								autoFocus: false,
								style: {
									zIndex: zIndex ?? 0 + 2,
								},
								PaperProps: {
									sx: {
										maxHeight: "20vh",
										width: 200,
										"@media screen and (max-width: 1366px) and (max-height: 768px)":
										{
											maxHeight:
												"15vh",
										},
									},
								},
							}}>
							{listValues?.map((v, index) => (
								<MenuItem
									key={`${v.value}-${index}-menu-item`}
									value={
										`${v.value}`
									}>
									<Checkbox
										key={`${v.value}-${index}-chk`}
										sx={{
											color: colors.slate,
											"&.Mui-checked":
											{
												color: colors.slate,
											},
										}}
										checked={
											selectedValues?.indexOf(
												`${v.value}`
											) > -1
										}
									/>
									<Box
										key={`${v.value}-${index}-name`}
										sx={{
											overflow:
												"hidden",
										}}>
										<Typography
											sx={{
												wordWrap:
													"break-word",
												whiteSpace:
													"normal",
												overflowWrap:
													"break-word",
											}}>
											{
												v.name
											}
										</Typography>
									</Box>
								</MenuItem>
							))}
						</Select>
						{formState && <FieldError key={`${childKey}-form-control-error`} formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
					</Grid>
					{showIndicator !== undefined && showIndicator() == true &&
						<Grid item xs={isIndicatorTakeSpace ? 1 : 0} sx={{ ...flexCenterProps }}>
							<DiffInheritanceIndicator />
						</Grid>
					}
				</Grid>
			</Grid>
		</FormControl>
	</Grid>;
}

export default memo(NewMultipleSelectSimpleFormControl);
